import { ReactNode, useState } from 'react';
import styled from 'styled-components';
import { addAlpha } from '@algoan/deep-dive/theme';
import { SmallInformationIcon, LikeIcon, ValidationIcon } from '../icons';
import { opinionSectionMessages } from './opinion-section-messages';
import { useIntl } from 'react-intl';
import { AmplitudeEvent, logAmplitudeEvent } from '@algoan/dashboard/analytics';
import { backgroundAnimation, validationContentAnimation } from './opinion-section-animation';
import { useHover, useLocalStorageState } from '@algoan/dashboard/utils';

export interface OpinionSectionProps {
  scope: string;
}

enum FeedbackType {
  LIKE = 'LIKE',
  DISLIKE = 'DISLIKE',
  NO_CHOICE = 'NO CHOICE',
}

export const OpinionSection: React.FC<OpinionSectionProps> = ({ scope }) => {
  const intl = useIntl();
  const [choice, setChoice] = useState(FeedbackType.NO_CHOICE);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [feedbackSent, setFeedbackSent] = useLocalStorageState<boolean>(`feedback_${scope}`, false);
  const [smallInformationIsHovering, smallInformationhoverRef] = useHover();
  const [likeIconIsHovering, likeIconHoverRef] = useHover();
  const [dontLikeIconIsHovering, dontLikeIconHoverRef] = useHover();

  const toggleChoice = (choiceClicked: FeedbackType): void =>
    choiceClicked === choice && choice !== FeedbackType.NO_CHOICE
      ? setChoice(FeedbackType.NO_CHOICE)
      : setChoice(choiceClicked);

  if (feedbackSent) {
    return null;
  }

  return (
    <S.Container isSubmitted={isSubmitted}>
      {(smallInformationIsHovering || likeIconIsHovering || dontLikeIconIsHovering) && (
        <S.TooltipContent isSmallInformation={smallInformationIsHovering}>
          {smallInformationIsHovering &&
            intl.formatMessage(opinionSectionMessages.tooltip, {
              likeIcon: <S.IconImage alt="like-icon" src="/images/icons/like-icon.png" />,
              dontLikeIcon: <S.IconImage alt="don't-like-icon" src="/images/icons/dont-like-icon.png" />,
            })}
          {likeIconIsHovering && intl.formatMessage(opinionSectionMessages.likeIconTooltip)}
          {dontLikeIconIsHovering && intl.formatMessage(opinionSectionMessages.dontLikeIconTooltip)}
        </S.TooltipContent>
      )}
      <S.BackgroundForAnimation isSubmitted={isSubmitted} />
      <S.ValidationContent isSubmitted={isSubmitted}>
        <ValidationIcon />
        <S.ValidationText>{intl.formatMessage(opinionSectionMessages.validationText)}</S.ValidationText>
      </S.ValidationContent>
      <S.Content isSubmitted={isSubmitted}>
        <S.SmallInformationContainer ref={smallInformationhoverRef}>
          <S.SmallInformationIcon />
        </S.SmallInformationContainer>
        <S.Text>
          {intl.formatMessage(opinionSectionMessages.text, {
            b: (str: ReactNode) => <S.MediumText>{str}</S.MediumText>,
          })}
        </S.Text>
        <S.LikeContainer onClick={() => toggleChoice(FeedbackType.LIKE)} choice={choice} ref={likeIconHoverRef}>
          <S.LikeIcon />
        </S.LikeContainer>
        <S.Divider />
        <S.DontLikeContainer
          onClick={() => toggleChoice(FeedbackType.DISLIKE)}
          choice={choice}
          ref={dontLikeIconHoverRef}
        >
          <S.DontLikeIcon />
        </S.DontLikeContainer>
        <S.Button
          onClick={() => {
            logAmplitudeEvent(AmplitudeEvent.FEEDBACK, {
              scope: scope,
              feedbackType: choice,
            });
            setIsSubmitted(true);
            setTimeout(() => {
              setFeedbackSent(true);
            }, 5000);
          }}
          disabled={choice === FeedbackType.NO_CHOICE}
        >
          {intl.formatMessage(opinionSectionMessages.button)}
        </S.Button>
      </S.Content>
    </S.Container>
  );
};

const S = {
  Container: styled.div<{ isSubmitted: boolean }>`
    position: relative;
    display: flex;
    align-items: center;
    height: 40px;
    width: 311px;
    padding: 0px 4px 0px 16px;
    color: ${({ theme }) => theme.white};
    background: ${({ theme, isSubmitted }) =>
      isSubmitted
        ? theme.white
        : `linear-gradient(
      91.08deg,
      ${theme.bluePeps} 23.46%,
      ${addAlpha(theme.bluePeps, 0.75)} 88.27%
    )`};
    border-radius: 21px;
    box-shadow: 0px 3px 20px ${({ theme }) => addAlpha(theme.algoanBlue, 0.4)};
  `,
  BackgroundForAnimation: styled.div<{ isSubmitted: boolean }>`
    display: ${({ isSubmitted }) => (isSubmitted ? 'flex' : 'none')};
    position: absolute;
    width: 0%;
    left: 0px;
    height: 100%;
    border-radius: 21px;
    background: linear-gradient(
      91.08deg,
      ${({ theme }) => theme.bluePeps} 23.46%,
      ${({ theme }) => addAlpha(theme.bluePeps, 0.75)} 88.27%
    );

    ${({ isSubmitted }) => (isSubmitted ? backgroundAnimation : '')}
  `,
  ValidationContent: styled.div<{ isSubmitted: boolean }>`
    position: absolute;
    display: ${({ isSubmitted }) => (isSubmitted ? 'flex' : 'none')};
    left: 0px;
    width: 100%;
    height: 100%;
    justify-content: center;
    align-items: center;
    opacity: 0;

    ${({ isSubmitted }) => (isSubmitted ? validationContentAnimation : '')}
  `,
  ValidationText: styled.div`
    margin-left: 8px;
    font-weight: ${({ theme }) => theme.fontWeight.medium};
    font-size: 13px;
    line-height: 115.4%;
    color: ${({ theme }) => theme.strongGrey};
  `,
  Content: styled.div<{ isSubmitted: boolean }>`
    display: ${({ isSubmitted }) => (isSubmitted ? 'none' : 'flex')};
    justify-content: space-between;
    align-items: center;
    height: 100%;
    width: 100%;
  `,
  SmallInformationIcon: styled(SmallInformationIcon)`
    &:hover {
      path {
        fill: ${({ theme }) => theme.white};
        box-shadow: 0px 1px 10px ${({ theme }) => addAlpha(theme.algoanBlue, 0.7)};
      }
    }
  `,
  Text: styled.div`
    margin-left: 8px;
    font-weight: ${({ theme }) => theme.fontWeight.semiBold};
    font-size: 14px;
    line-height: 14px;
  `,
  MediumText: styled.div`
    font-weight: ${({ theme }) => theme.fontWeight.medium};
    font-size: 10px;
    line-height: 12px;
  `,
  LikeIcon: styled(LikeIcon)`
    margin-left: 2px;
    margin-bottom: 2px;
  `,
  LikeContainer: styled.div<{ choice: FeedbackType }>`
    margin-left: 20px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;

    background: ${({ theme, choice }) => (choice === FeedbackType.LIKE ? theme.white : 'transparent')};
    path {
      fill: ${({ theme, choice }) =>
        choice === FeedbackType.LIKE ? theme.scoreIndicatorColors.positiveValue : theme.white};
    }

    &:hover {
      background: ${({ theme, choice }) => (choice === FeedbackType.LIKE ? theme.white : addAlpha(theme.white, 0.2))};
      path {
        fill: ${({ theme }) => theme.scoreIndicatorColors.positiveValue};
      }
    }
  `,
  Divider: styled.div`
    height: 13px;
    margin: 0px 8px;
    border-left: 1px solid ${({ theme }) => addAlpha(theme.white, 0.4)};
  `,
  DontLikeContainer: styled.div<{ choice: FeedbackType }>`
    transform: scaleY(-1);
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;

    cursor: pointer;

    background: ${({ theme, choice }) => (choice === FeedbackType.DISLIKE ? theme.white : 'transparent')};
    path {
      fill: ${({ theme, choice }) =>
        choice === FeedbackType.DISLIKE ? theme.scoreIndicatorColors.negativeValue : theme.white};
    }

    &:hover {
      background: ${({ theme, choice }) =>
        choice === FeedbackType.DISLIKE ? theme.white : addAlpha(theme.white, 0.2)};
      path {
        fill: ${({ theme }) => theme.scoreIndicatorColors.negativeValue};
      }
    }
  `,
  DontLikeIcon: styled(LikeIcon)`
    margin-bottom: 2px;
  `,
  Button: styled.button`
    border: none;
    margin-left: 20px;
    padding: 8px 12px;
    font-weight: ${({ theme }) => theme.fontWeight.medium};
    font-size: 14px;
    line-height: 115.4%;
    background: ${({ theme }) => theme.white};
    border-radius: 21px;
    color: ${({ theme }) => theme.bluePeps};
    cursor: pointer;

    &[disabled] {
      opacity: 0.4;
      cursor: initial;
    }

    &:hover {
      box-shadow: 0px 1px 10px ${({ theme }) => addAlpha(theme.algoanBlue, 0.7)};
    }
  `,
  SmallInformationContainer: styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
  `,
  IconImage: styled.img`
    width: 13px;
    height: 11px;
  `,
  LikeIconTooltip: styled(LikeIcon)``,
  DontLikeIconTooltip: styled(LikeIcon)``,
  TooltipContent: styled.div<{ isSmallInformation: boolean }>`
    position: absolute;
    width: 315px;
    padding: ${({ isSmallInformation }) => (isSmallInformation ? '8px' : '12px')};
    text-align: ${({ isSmallInformation }) => (isSmallInformation ? 'initial' : 'center')};
    left: 0px;
    bottom: 48px;
    background: ${({ theme }) => theme.white};
    box-shadow: 0px 3px 20px rgba(0, 0, 0, 0.2);
    border-radius: 4px;
    color: ${({ theme }) => theme.middleGrey};
    font-weight: ${({ theme }) => theme.fontWeight.regular};
    font-size: 12px;
    line-height: 15px;
    z-index: 100000;
  `,
};
